import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Form, Table, Container, InputGroup } from "react-bootstrap";
import { FiSearch, FiEdit2, FiPlus } from "react-icons/fi";
import { createDocument, getAllDocuments, updateDocument } from "../../../api/onboarddeboard";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CATEGORIES = ["Identity", "Employment", "Education", "Financial", "Benefits", "Other"];

const Document = () => {
  const token = localStorage.getItem("token");

  const [documents, setDocuments]   = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal]   = useState(false);
  const [editId, setEditId]         = useState(null);

  const [documentName, setDocumentName] = useState("");
  const [category, setCategory]         = useState("");
  const [mandatory, setMandatory]       = useState(false);
  const [description, setDescription]   = useState("");

  const [nameError, setNameError] = useState("");

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await getAllDocuments(token);
      if (res.status === 200) setDocuments(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const resetForm = () => {
    setDocumentName("");
    setCategory("");
    setMandatory(false);
    setDescription("");
    setNameError("");
  };

  const handleOpenAdd = () => {
    setEditId(null);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (doc) => {
    setEditId(doc.id);
    setDocumentName(doc.documentName || "");
    setCategory(doc.category || "");
    setMandatory(
      typeof doc.mandatory === "boolean"
        ? doc.mandatory
        : doc.mandatory === "Required" || doc.mandatory === "true" || doc.mandatory === true
    );
    setDescription(doc.description || "");
    setNameError("");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    resetForm();
    setEditId(null);
  };

  const validateName = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "Document Name is required";
    if (/^[0-9]+$/.test(trimmed)) return "Document Name cannot be numbers only";
    if (/^[^a-zA-Z0-9]+$/.test(trimmed)) return "Cannot contain only special characters";
    if (!/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(trimmed)) return "Must contain only alphabets";
    return "";
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    setDocumentName(val);
    setNameError(val === "" ? "" : validateName(val));
  };

  const handleSubmit = async () => {
    const nameErr = validateName(documentName);
    if (nameErr) { setNameError(nameErr); return; }

    const isDuplicate = documents.some(
      (d) =>
        d.documentName.trim().toLowerCase() === documentName.trim().toLowerCase() &&
        d.id !== editId
    );
    if (isDuplicate) {
      toast.warn("Document with this name already exists");
      return;
    }

    if (editId) {
      const existing = documents.find((d) => d.id === editId);
      const existingMandatory =
        typeof existing?.mandatory === "boolean"
          ? existing.mandatory
          : existing?.mandatory === "Required" || existing?.mandatory === "true";
      if (
        existing &&
        existing.documentName.trim() === documentName.trim() &&
        existing.category === category &&
        existingMandatory === mandatory &&
        (existing.description || "") === description
      ) {
        toast.warn("No changes found for updation");
        return;
      }
    }

    const payload = {
      documentName: documentName.trim(),
      category,
      mandatory,
      description,
    };

    try {
      if (editId) {
        await updateDocument(editId, payload, token);
        toast.success("Document updated successfully");
      } else {
        await createDocument(payload, token);
        toast.success("Document created successfully");
      }
      handleClose();
      fetchDocuments();
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const filtered = documents.filter((d) =>
    d.documentName.toLowerCase().includes(searchText.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try { return new Date(dateStr).toISOString().split("T")[0]; }
    catch { return dateStr; }
  };

  // ✅ FIXED: handles boolean true/false AND string "true"/"false"
  const displayMandatory = (val) => {
    if (typeof val === "boolean") return val ? "Required" : "Optional";
    if (val === "true") return "Required";
    if (val === "false") return "Optional";
    return val || "—";
  };

  return (
    <Container fluid className="p-4">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h5 className="page-main-heading">Documents</h5>
          <small className="page-sub-heading">Manage required employee documents</small>
        </div>

        <Button
          variant="primary"
          onClick={handleOpenAdd}
          style={{
            borderRadius: 8,
            fontWeight: 500,
            background: "#2E5CB8",
            borderColor: "#2E5CB8",
          }}
        >
          <FiPlus className="me-1" /> Add Document
        </Button>
      </div>

      <div className="mb-3 mt-3" style={{ maxWidth: 340 }}>
        <InputGroup>
          <InputGroup.Text style={{ background: "#fff" }}>
            <FiSearch color="#888" />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search documents..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ borderLeft: "none" }}
          />
        </InputGroup>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Table hover responsive className="mb-0 align-middle">
          <thead>
            <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
              <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444" }}>Document Name</th>
              <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444" }}>Category</th>
              <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Mandatory</th>
              <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Last Updated</th>
              <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-5 text-muted">No documents found</td>
              </tr>
            ) : (
              filtered.map((doc) => (
                <tr key={doc.id}>
                  <td style={{ padding: "14px 20px", fontWeight: 500 }}>{doc.documentName}</td>
                  <td style={{ padding: "14px 20px", color: "#555" }}>{doc.category || "—"}</td>
                  <td style={{ padding: "14px 20px", textAlign: "center" }}>
                    <span style={{
                      background: "#f0f0f0", color: "#555",
                      borderRadius: 20, padding: "3px 14px",
                      fontSize: 13, fontWeight: 500,
                    }}>
                      {displayMandatory(doc.mandatory)}
                    </span>
                  </td>
                  <td style={{ padding: "14px 20px", textAlign: "center", color: "#555" }}>
                    {formatDate(doc.lastUpdated || doc.updatedAt)}
                  </td>
                  <td style={{ padding: "14px 20px", textAlign: "center" }}>
                    <button
                      onClick={() => handleEdit(doc)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#4466ee" }}
                      title="Edit"
                    >
                      <FiEdit2 size={17} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={handleClose} centered size="md">
        <Modal.Header closeButton style={{ borderBottom: "1px solid #eee", paddingBottom: 10 }}>
          <div>
            <Modal.Title style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: 2 }}>
              {editId ? "Edit Document" : "Add Document"}
            </Modal.Title>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 400 }}>
              {editId
                ? "Update the document details below."
                : "Fill in the details to add a new document."}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-4">

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ fontSize: 14 }}>
              Document Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Aadhaar Card"
              value={documentName}
              onChange={handleNameChange}
              style={{ borderRadius: 8, height: 44 }}
              autoFocus
            />
            {nameError && <small className="text-danger mt-1 d-block">{nameError}</small>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ fontSize: 14 }}>Category</Form.Label>
            <Form.Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ borderRadius: 8, height: 44 }}
            >
              <option value="">Select category</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="mandatory-checkbox"
              label="Mandatory document"
              checked={mandatory}
              onChange={(e) => setMandatory(e.target.checked)}
              style={{ fontSize: 14 }}
            />
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label className="fw-semibold" style={{ fontSize: 14 }}>
              Description{" "}
              <span style={{ fontWeight: 400, color: "#9ca3af", fontSize: 13 }}>(optional)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Brief description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ borderRadius: 8, resize: "vertical" }}
            />
          </Form.Group>

        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #eee" }}>
          <Button
            variant="secondary"
            onClick={handleClose}
            style={{ borderRadius: 7, minWidth: 90 }}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleSubmit}
            style={{
              borderRadius: 7,
              minWidth: 90,
              background: "#2E5CB8",
              borderColor: "#2E5CB8",
            }}
          >
            {editId ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </Container>
  );
};

export default Document;